import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { useConfirm } from "@/context/ConfirmContext";
import { StatusBadge } from "@/components/shared/Badges";
import { fmtDate } from "@/components/shared/DashKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, Eye, X, RefreshCw, CreditCard } from "lucide-react";

const sLabel = (t) => ({
  pending: t("admin.afp.st.pending", "En attente"), completed: t("admin.afp.st.completed", "Confirmé"),
  success: t("admin.afp.st.success", "Succès"), failed: t("admin.afp.st.failed", "Échoué"),
  cancelled: t("admin.afp.st.cancelled", "Annulé"), unknown: t("admin.afp.st.unknown", "Inconnu"),
});

export default function AdminAfriPay() {
  const { t } = useContent();
  const STATUSES = sLabel(t);
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (status) p.set("status", status);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    api.get(`/admin/payments/afripay${p.toString() ? `?${p}` : ""}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [q, status, dateFrom, dateTo]);

  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [load]);

  return (
    <div>
      <AHeader title={t("admin.afp.title", "AfriPay — Carte Bancaire")} subtitle={t("admin.afp.subtitle", "Transactions réelles par carte bancaire (recharges de solde).")} />

      <ACard className="mb-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="afripay-filters">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input data-testid="afripay-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("admin.afp.search_ph", "Recherche : réf. TWP, e-mail client, ID…")} className={`${inputDark} pl-9`} />
          </div>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="afripay-status-filter" className="border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.afp.status_ph", "Statut")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("admin.afp.all_statuses", "Tous les statuts")}</SelectItem>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <input data-testid="afripay-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputDark} title={t("admin.afp.from", "Du")} />
            <input data-testid="afripay-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputDark} title={t("admin.afp.to", "Au")} />
          </div>
        </div>
      </ACard>

      <ACard>
        {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <div className="py-10 text-center text-slate-400" data-testid="afripay-empty">{t("admin.afp.empty", "Aucune transaction AfriPay.")}</div>
          : (
            <ATable head={[t("admin.afp.th.ref", "Réf. TWP"), t("admin.afp.th.client", "Client"), t("admin.afp.th.amount", "Montant"), t("admin.afp.th.currency", "Devise"), t("admin.afp.th.afripay_ref", "Réf. AfriPay"), t("admin.afp.th.status", "Statut"), t("admin.afp.th.confirmed", "Confirmé le"), t("admin.afp.th.date", "Date"), ""]}>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60 last:border-0" data-testid={`afripay-row-${p.id}`}>
                  <td className="px-3 py-3 font-mono text-xs text-slate-200">{p.transaction_ref || p.id?.slice(0, 8)}</td>
                  <td className="px-3 py-3 text-slate-400">{p.user_email}</td>
                  <td className="px-3 py-3 font-medium"><Amount value={p.amount} /></td>
                  <td className="px-3 py-3 text-slate-400">{p.currency}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-400">{p.afripay_ref || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={p.status === "completed" || p.credited ? "completed" : p.status} /></td>
                  <td className="px-3 py-3 text-slate-400">{p.confirmed_at ? fmtDate(p.confirmed_at) : "—"}</td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(p.created_at)}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => setDetail(p)} data-testid={`afripay-detail-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800"><Eye className="h-3 w-3" /> {t("admin.afp.details", "Détails")}</button>
                  </td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>

      {detail && <AfripayDetail p={detail} onClose={() => setDetail(null)} onReconciled={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function AfripayDetail({ p, onClose, onReconciled }) {
  const { t } = useContent();
  const STATUSES = sLabel(t);
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const reconcile = async () => {
    const desc = t("admin.afp.credit_desc", "Créditer manuellement {amount} $ pour {email} ?\nRéf. : {ref}")
      .replace("{amount}", p.amount).replace("{email}", p.user_email).replace("{ref}", p.transaction_ref);
    if (!(await confirm({ title: t("admin.afp.credit_title", "Créditer manuellement"), description: desc, confirmLabel: t("admin.afp.credit_ok", "Créditer"), cancelLabel: t("admin.afp.cancel", "Annuler") }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/afripay/reconcile", { transaction_ref: p.transaction_ref });
      if (data.credited) toast.success(`${t("admin.afp.credited", "Crédité :")} ${data.amount} $`);
      else toast.info(data.message || t("admin.afp.already", "Déjà crédité"));
      onReconciled();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 border-b border-slate-800/50 py-2 text-sm last:border-0">
      <span className="text-slate-400">{label}</span><span className="text-right font-medium text-slate-100 break-all">{value ?? "—"}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="afripay-detail-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white"><CreditCard className="h-5 w-5 text-accent" /> {t("admin.afp.tx_title", "Transaction AfriPay")}</h3>
          <button onClick={onClose} data-testid="afripay-detail-close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <Row label={t("admin.afp.row.ref", "Réf. TWP")} value={p.transaction_ref} />
        <Row label={t("admin.afp.row.tx_id", "ID transaction")} value={p.id} />
        <Row label={t("admin.afp.row.client", "Client")} value={p.user_email} />
        <Row label={t("admin.afp.row.amount_credited", "Montant crédité")} value={`${p.amount} ${p.currency}`} />
        <Row label={t("admin.afp.row.fee", "Frais TWP ({pct}%)").replace("{pct}", p.fee_percent ?? 5)} value={p.fee_amount != null ? `${p.fee_amount} ${p.currency}` : "—"} />
        <Row label={t("admin.afp.row.total_debited", "Total débité")} value={p.charge_amount != null ? `${p.charge_amount} ${p.currency}` : "—"} />
        <Row label={t("admin.afp.row.afripay_ref", "Réf. AfriPay")} value={p.afripay_ref} />
        <Row label={t("admin.afp.row.status", "Statut")} value={<StatusBadge status={p.status === "completed" || p.credited ? "completed" : p.status} />} />
        <Row label={t("admin.afp.row.credited_wallet", "Crédité au Wallet")} value={p.credited ? t("admin.afp.yes", "Oui") : t("admin.afp.no", "Non")} />
        <Row label={t("admin.afp.row.created", "Créé le")} value={fmtDate(p.created_at)} />
        <Row label={t("admin.afp.row.confirmed", "Confirmé le")} value={p.confirmed_at ? fmtDate(p.confirmed_at) : "—"} />
        {p.error && <div className="mt-3 rounded-lg border border-red-900/50 bg-red-950/40 px-3 py-2 text-xs text-red-300" data-testid="afripay-detail-error">{t("admin.afp.error", "Erreur :")} {p.error}</div>}

        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-slate-400">{t("admin.afp.status_history", "Historique du statut")}</p>
          <div className="space-y-1" data-testid="afripay-detail-history">
            {(p.status_history || []).map((h, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-300"><span>{STATUSES[h.status] || h.status}</span><span className="text-slate-500">{h.at ? fmtDate(h.at) : "—"}</span></div>
            ))}
          </div>
        </div>

        {!p.credited && (
          <button onClick={reconcile} disabled={busy} data-testid="afripay-detail-reconcile"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("admin.afp.reconcile_btn", "Créditer manuellement (réconciliation)")}
          </button>
        )}
      </div>
    </div>
  );
}
